/**
 * SnapshotManager Component
 *
 * UI for creating, viewing, and comparing portfolio snapshots
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { usePortfolioSnapshots, type PortfolioSnapshot, type SnapshotComparison } from '../../hooks/usePortfolioSnapshots';
import type { Token, Prices } from '../../types';

interface SnapshotManagerProps {
  tokens: Token[];
  currentPrices: Prices;
  isOpen: boolean;
  onClose: () => void;
}

export function SnapshotManager({ tokens, currentPrices, isOpen, onClose }: SnapshotManagerProps) {
  const { t } = useTranslation();
  const {
    snapshots,
    createSnapshot,
    deleteSnapshot,
    updateSnapshot,
    compareSnapshots,
    exportSnapshots,
    importSnapshots,
    clearAllSnapshots,
    snapshotCount,
    maxSnapshots,
  } = usePortfolioSnapshots();

  const [newSnapshotName, setNewSnapshotName] = useState('');
  const [newSnapshotNotes, setNewSnapshotNotes] = useState('');
  const [selectedSnapshots, setSelectedSnapshots] = useState<string[]>([]);
  const [comparison, setComparison] = useState<SnapshotComparison | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');

  if (!isOpen) return null;

  const handleCreateSnapshot = () => {
    const name = newSnapshotName.trim() || `Snapshot ${new Date().toLocaleDateString()}`;
    createSnapshot(tokens, currentPrices, name, newSnapshotNotes.trim() || undefined);
    setNewSnapshotName('');
    setNewSnapshotNotes('');
  };

  const handleCompare = () => {
    if (selectedSnapshots.length === 2) {
      const result = compareSnapshots(selectedSnapshots[0], selectedSnapshots[1]);
      setComparison(result);
    }
  };

  const handleSelectSnapshot = (id: string) => {
    setSelectedSnapshots(prev => {
      if (prev.includes(id)) {
        return prev.filter(s => s !== id);
      }
      if (prev.length < 2) {
        return [...prev, id];
      }
      return [prev[1], id]; // Replace oldest selection
    });
  };

  const handleExport = () => {
    const data = exportSnapshots();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-snapshots-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    if (importSnapshots(importData)) {
      setShowImportModal(false);
      setImportData('');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--card)',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '85vh',
          overflow: 'hidden',
          zIndex: 10000,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icons.Camera size={24} />
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
                {t('snapshots.title', 'Portfolio Snapshots')}
              </h2>
              <p style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {snapshotCount}/{maxSnapshots} {t('snapshots.saved', 'saved')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text)',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
            }}
          >
            <Icons.X size={20} />
          </button>
        </div>

        {/* Create Snapshot Section */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--hover)',
          }}
        >
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                {t('snapshots.name', 'Snapshot Name')}
              </label>
              <input
                type="text"
                value={newSnapshotName}
                onChange={(e) => setNewSnapshotName(e.target.value)}
                placeholder={t('snapshots.namePlaceholder', 'e.g., Before rebalance')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  fontSize: '14px',
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                {t('snapshots.notes', 'Notes (optional)')}
              </label>
              <input
                type="text"
                value={newSnapshotNotes}
                onChange={(e) => setNewSnapshotNotes(e.target.value)}
                placeholder={t('snapshots.notesPlaceholder', 'Add a note...')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  fontSize: '14px',
                }}
              />
            </div>
            <button
              onClick={handleCreateSnapshot}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--accent)',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              <Icons.Camera size={16} />
              {t('snapshots.create', 'Take Snapshot')}
            </button>
          </div>
        </div>

        {/* Actions Bar */}
        <div
          style={{
            padding: '12px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={handleCompare}
            disabled={selectedSnapshots.length !== 2}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: selectedSnapshots.length === 2 ? 'var(--accent)' : 'var(--card)',
              color: selectedSnapshots.length === 2 ? 'white' : 'var(--muted)',
              fontSize: '13px',
              cursor: selectedSnapshots.length === 2 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Icons.GitCompare size={14} />
            {t('snapshots.compare', 'Compare')} ({selectedSnapshots.length}/2)
          </button>
          <button
            onClick={handleExport}
            disabled={snapshots.length === 0}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text)',
              fontSize: '13px',
              cursor: snapshots.length > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Icons.Download size={14} />
            {t('snapshots.export', 'Export')}
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text)',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Icons.Upload size={14} />
            {t('snapshots.import', 'Import')}
          </button>
          {snapshots.length > 0 && (
            <button
              onClick={() => {
                if (confirm(t('snapshots.clearConfirm', 'Are you sure you want to delete all snapshots?'))) {
                  clearAllSnapshots();
                }
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #ef4444',
                background: 'transparent',
                color: '#ef4444',
                fontSize: '13px',
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
            >
              {t('snapshots.clearAll', 'Clear All')}
            </button>
          )}
        </div>

        {/* Comparison Result */}
        {comparison && (
          <div
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--hover)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600 }}>
                📊 {t('snapshots.comparisonResult', 'Comparison Result')}
              </h3>
              <button
                onClick={() => setComparison(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer' }}
              >
                <Icons.X size={16} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div style={{ textAlign: 'center', padding: '12px', background: 'var(--card)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('snapshots.valueDiff', 'Value Change')}</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: comparison.valueDiff >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {comparison.valueDiff >= 0 ? '+' : ''}{formatCurrency(comparison.valueDiff)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: comparison.valueDiffPercent >= 0 ? '#10b981' : '#ef4444'
                }}>
                  ({comparison.valueDiffPercent >= 0 ? '+' : ''}{comparison.valueDiffPercent.toFixed(2)}%)
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: 'var(--card)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('snapshots.pnlDiff', 'P&L Change')}</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: comparison.pnlDiff >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {comparison.pnlDiff >= 0 ? '+' : ''}{formatCurrency(comparison.pnlDiff)}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: 'var(--card)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('snapshots.tokenChanges', 'Token Changes')}</div>
                <div style={{ fontSize: '13px', marginTop: '4px' }}>
                  <span style={{ color: '#10b981' }}>+{comparison.tokenChanges.added.length}</span>
                  {' / '}
                  <span style={{ color: '#ef4444' }}>-{comparison.tokenChanges.removed.length}</span>
                  {' / '}
                  <span style={{ color: '#f59e0b' }}>~{comparison.tokenChanges.changed.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Snapshots List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {snapshots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
              <Icons.Camera size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>{t('snapshots.empty', 'No snapshots yet. Take your first snapshot to track portfolio changes!')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {snapshots.map((snapshot) => (
                <div
                  key={snapshot.id}
                  onClick={() => handleSelectSnapshot(snapshot.id)}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: selectedSnapshots.includes(snapshot.id)
                      ? '2px solid var(--accent)'
                      : '1px solid var(--border)',
                    background: selectedSnapshots.includes(snapshot.id) ? 'var(--hover)' : 'var(--card)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      {editingId === snapshot.id ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onBlur={() => {
                            updateSnapshot(snapshot.id, { name: editName });
                            setEditingId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateSnapshot(snapshot.id, { name: editName });
                              setEditingId(null);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid var(--accent)',
                            background: 'var(--card)',
                            color: 'var(--text)',
                            fontSize: '16px',
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <h4
                          style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setEditingId(snapshot.id);
                            setEditName(snapshot.name);
                          }}
                        >
                          {snapshot.name}
                        </h4>
                      )}
                      <p style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {formatDate(snapshot.createdAt)} • {snapshot.tokenCount} tokens • {snapshot.positionCount} positions
                      </p>
                      {snapshot.notes && (
                        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px', fontStyle: 'italic' }}>
                          "{snapshot.notes}"
                        </p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>
                        {formatCurrency(snapshot.totalValueUSD)}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: snapshot.totalPnL >= 0 ? '#10b981' : '#ef4444'
                      }}>
                        {snapshot.totalPnL >= 0 ? '+' : ''}{formatCurrency(snapshot.totalPnL)}
                        {' '}({snapshot.totalPnLPercent >= 0 ? '+' : ''}{snapshot.totalPnLPercent.toFixed(2)}%)
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(t('snapshots.deleteConfirm', 'Delete this snapshot?'))) {
                          deleteSnapshot(snapshot.id);
                        }
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--muted)',
                        cursor: 'pointer',
                        padding: '4px',
                        marginLeft: '12px',
                      }}
                    >
                      <Icons.Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              zIndex: 10001,
            }}
            onClick={() => setShowImportModal(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'var(--card)',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              zIndex: 10002,
            }}
          >
            <h3 style={{ marginBottom: '16px' }}>{t('snapshots.importTitle', 'Import Snapshots')}</h3>
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder={t('snapshots.importPlaceholder', 'Paste JSON data here...')}
              style={{
                width: '100%',
                height: '200px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                background: 'var(--hover)',
                color: 'var(--text)',
                fontSize: '13px',
                fontFamily: 'monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowImportModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleImport}
                disabled={!importData.trim()}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'var(--accent)',
                  color: 'white',
                  cursor: importData.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('snapshots.import', 'Import')}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Small button to trigger snapshot creation from portfolio view
 */
export function SnapshotButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      title={t('snapshots.takeSnapshot', 'Take a snapshot of your portfolio')}
      style={{
        padding: '8px 16px',
        borderRadius: '8px',
        border: '1px solid var(--border)',
        background: 'var(--card)',
        color: 'var(--text)',
        fontSize: '13px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease',
      }}
    >
      <Icons.Camera size={16} />
      {t('snapshots.snapshot', 'Snapshot')}
    </button>
  );
}
